import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';

interface Shortcut {
  keys: string;
  callback: () => void;
}

@Injectable({
  providedIn: 'root'
})
export class ShortcutHandlerService {
  private shortcuts: Shortcut[] = [];
  private keydown$ = new Subject<KeyboardEvent>();
  private os: BehaviorSubject<string> = new BehaviorSubject<string>(this.getOS());
  os$ = this.os.asObservable();

  constructor() {
    window.addEventListener('keydown', (event) => this.keydown$.next(event));
    this.keydown$.subscribe((event) => this.handleKeydown(event));
    this.os.next(this.getOS());
  }

  registerShortcut(keys: string, callback: () => void) {
    this.shortcuts.push({ keys, callback });
  }

  getOS(): string {
    const userAgent = window.navigator.userAgent;
    const platform = window.navigator.platform;
    const macosPlatforms = ['Macintosh', 'MacIntel', 'MacPPC', 'Mac68K'];
    const windowsPlatforms = ['Win32', 'Win64', 'Windows', 'WinCE'];
    const iosPlatforms = ['iPhone', 'iPad', 'iPod'];

    if (macosPlatforms.indexOf(platform) !== -1) {
      return 'Mac';
    } else if (iosPlatforms.indexOf(platform) !== -1) {
      return 'iOS';
    } else if (windowsPlatforms.indexOf(platform) !== -1) {
      return 'Windows';
    } else if (/Android/.test(userAgent)) {
      return 'Android';
    } else if (/Linux/.test(platform)) {
      return 'Linux';
    }

    return 'Unknown';
  }

  registerAllDynamicShortcuts() {
    const dynamicShortcuts = this.getDynamicShortcuts();
    dynamicShortcuts.forEach(shortcut => {
      this.registerShortcut(shortcut.keys, () => {
        console.log(`Shortcut triggered: ${shortcut.description}`);
        // Implement the actual callback functionality here
      });
    });
  }

  getBrowser() {
    const userAgent = window.navigator.userAgent;
    const browser = userAgent.split('/')[0];
    return browser;
  }

  getShortcutPrefix(letter: string) {
    switch (this.os.value) {
      case 'Windows':
        return 'ctrl+'+letter;
      case 'Mac':
        return '⌘+'+letter;
      case 'iOS':
        return '⌘+'+letter;
      case 'Linux':
        return 'ctrl+'+letter;
      default:
        return '';
    }
  }

  getDynamicShortcuts(): { keys: string, description: string }[] {
    const os = this.getOS();
    const browser = this.getBrowser();
    let shortcuts = [
      { baseKeys: 'S', description: 'Save the current document' },
      { baseKeys: 'O', description: 'Open a new document' },
      { baseKeys: 'K', description: 'Use Quick Find' },
      { baseKeys: 'Z', description: 'Undo last action' },
      { baseKeys: 'Q', description: 'Toggle Debug Mode' },
      // Add more base shortcuts as needed
    ];

    return shortcuts.map(shortcut => {
      return {
        keys: this.getShortcutPrefix(shortcut.baseKeys) + (browser === 'Chrome' ? '+shift' : ''),
        description: shortcut.description
      };
    });
  }

  private handleKeydown(event: KeyboardEvent) {
    const pressedKeys = `${event.ctrlKey ? 'ctrl+' : ''}${event.key}`.toLowerCase();
    this.shortcuts.forEach(shortcut => {
      if (shortcut.keys === pressedKeys) {
        event.preventDefault();
        shortcut.callback();
      }
    });
  }
}